/**
 * Space Board Columns
 *
 * Normalizes Space status and kanban templates into the shared board-view
 * column contract.
 *
 * Primary responsibilities:
 * - Convert Space templates into canonical board columns
 * - Keep board labels, colors, and status mappings consistent
 *
 * Dependencies:
 * - types/view-templates
 */

import type {
  KanbanTemplate,
  StatusTemplate
} from "@/types/view-templates"

export type SpaceBoardColumn = {
  id: string
  title: string
  status: string
  statuses: string[]
  color: string
  limit?: number
}

const DEFAULT_COLOR = "bg-slate-100 dark:bg-slate-900/30"

const COLOR_CLASS_BY_TOKEN: Record<string, string> = {
  slate: "bg-slate-100 dark:bg-slate-900/30",
  gray: "bg-slate-100 dark:bg-slate-900/30",
  blue: "bg-blue-100 dark:bg-blue-900/30",
  green: "bg-green-100 dark:bg-green-900/30",
  yellow: "bg-yellow-100 dark:bg-yellow-900/30",
  orange: "bg-orange-100 dark:bg-orange-900/30",
  red: "bg-red-100 dark:bg-red-900/30",
  purple: "bg-purple-100 dark:bg-purple-900/30",
  pink: "bg-pink-100 dark:bg-pink-900/30"
}

const DEFAULT_BOARD_COLUMNS: SpaceBoardColumn[] = [
  {
    id: "todo",
    title: "To Do",
    status: "todo",
    statuses: ["todo", "pending", "backlog", "new"],
    color: COLOR_CLASS_BY_TOKEN.slate
  },
  {
    id: "in_progress",
    title: "In Progress",
    status: "in_progress",
    statuses: ["in_progress", "inprogress", "doing", "active", "started"],
    color: COLOR_CLASS_BY_TOKEN.blue
  },
  {
    id: "done",
    title: "Done",
    status: "done",
    statuses: ["done", "completed", "finished"],
    color: COLOR_CLASS_BY_TOKEN.green
  }
]

function normalizeStatusValue(value: string) {
  return value.toLowerCase().replace(/[-\s]/g, "_")
}

function uniqueStatusValues(values: Array<string | null | undefined>) {
  const seen = new Set<string>()
  const normalizedValues: string[] = []

  for (const value of values) {
    if (!value) {
      continue
    }

    const normalized = normalizeStatusValue(value)

    if (!seen.has(normalized)) {
      seen.add(normalized)
      normalizedValues.push(normalized)
    }
  }

  return normalizedValues
}

function getColumnColor(colorToken?: string | null) {
  if (!colorToken) {
    return DEFAULT_COLOR
  }

  if (colorToken.startsWith("bg-")) {
    return colorToken
  }

  return COLOR_CLASS_BY_TOKEN[colorToken] || DEFAULT_COLOR
}

function getStatusValuesFromTemplate(
  statusTemplate: StatusTemplate | null | undefined,
  statusIds: string[] | null | undefined
) {
  if (!statusTemplate || !statusIds?.length) {
    return []
  }

  return uniqueStatusValues(
    statusIds.flatMap((statusId) => {
      const status = statusTemplate.statuses.find((candidate) => candidate.id === statusId)
      if (!status) {
        return [statusId]
      }

      return status.statusValues?.length ? status.statusValues : [status.id]
    })
  )
}

export function buildSpaceBoardColumns({
  statusTemplate,
  kanbanTemplate
}: {
  statusTemplate?: StatusTemplate | null
  kanbanTemplate?: KanbanTemplate | null
}) {
  if (kanbanTemplate?.columns?.length) {
    return kanbanTemplate.columns.map((column) => {
      const statuses = getStatusValuesFromTemplate(statusTemplate, column.statusIds)
      const fallbackStatuses = uniqueStatusValues(column.statusMappings || [])
      const mappedStatuses =
        statuses.length > 0
          ? statuses
          : fallbackStatuses.length > 0
            ? fallbackStatuses
            : [normalizeStatusValue(column.id)]

      return {
        id: column.id,
        title: column.title,
        status: mappedStatuses[0],
        statuses: mappedStatuses,
        color: getColumnColor(column.bgColor || column.color),
        limit: column.wipLimit
      }
    })
  }

  if (statusTemplate?.statuses?.length) {
    return [...statusTemplate.statuses]
      .sort((left, right) => left.order - right.order)
      .map((status) => {
        const mappedStatuses = uniqueStatusValues(
          status.statusValues?.length ? status.statusValues : [status.id]
        )

        return {
          id: status.id,
          title: status.name,
          status: mappedStatuses[0] || normalizeStatusValue(status.id),
          statuses: mappedStatuses.length > 0 ? mappedStatuses : [normalizeStatusValue(status.id)],
          color: getColumnColor(status.color)
        }
      })
  }

  return DEFAULT_BOARD_COLUMNS
}
